import React, { useEffect, useState, useRef } from "react";
import "../ModalStyles.css"; // Create this CSS file for styling

const RenameModal = ({ isOpen, onClose, onRename, fileName, loading }) => {
  const [newName, setNewName] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const inputRef = useRef(null);

  // Reset state when modal opens with a new file
  useEffect(() => {
    if (isOpen && !isInitialized) {
      setNewName(fileName);
      setIsInitialized(true);

      // Focus input when modal opens
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 100);
    }
  }, [isOpen, fileName, isInitialized]);

  // Reset initialization state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsInitialized(false);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    setNewName(e.target.value);
  };

  const handleSubmit = () => {
    if (newName.trim() !== "" && newName !== fileName && !loading) {
      onRename(newName.trim());
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !isSaveDisabled) {
      handleSubmit();
    }
  };

  const isSaveDisabled =
    newName.trim() === "" || newName === fileName || loading;
  const saveButtonBgColor = !isSaveDisabled ? "#C95EBE" : "#9CA3AF";

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg py-4 px-6 relative font-outfit rename-modal-container">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
          disabled={loading}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Rename Document
        </h2>

        <p className="text-sm text-gray-600 mb-4">
          Create a Custom Name for your Documents
        </p>

        {/* Input Row */}
        <div className="flex items-center gap-2 mb-6">
          <h3 className="text-sm font-medium text-gray-900 min-w-[40px]">
            Title *
          </h3>
          <input
            ref={inputRef}
            type="text"
            value={newName}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Enter new name"
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent text-sm leading-tight h-[42px] min-h-[42px] max-h-[42px] transition-colors duration-200 hover:border-gray-400 rename-modal-input"
            style={{
              fontSize: "14px",
              lineHeight: "1.25",
            }}
            disabled={loading}
          />
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-center gap-3 rename-modal-buttons">
          <button
            onClick={onClose}
            disabled={loading}
            className="w-[20%] py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed rename-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaveDisabled}
            className="w-[25%] py-2.5 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm rename-modal-save"
            style={{
              backgroundColor: saveButtonBgColor,
              cursor: isSaveDisabled ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RenameModal;